import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@components/lib/utils';

import {
  type NavSection,
  isNavItemActive,
  isSectionActive,
  navItemClass,
} from './navConfig';

const CLOSE_DELAY_MS = 120;

const focusables = (root: HTMLElement | null): HTMLElement[] =>
  Array.from(root?.querySelectorAll<HTMLElement>('a[href]') ?? []);

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The first tabbable element after `root` in document order. */
const nextTabbableAfter = (root: HTMLElement): HTMLElement | undefined =>
  Array.from(document.querySelectorAll<HTMLElement>(TABBABLE)).find(
    (el) =>
      !root.contains(el) &&
      // eslint-disable-next-line no-bitwise -- DOM position bitmask
      !!(root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
  );

/**
 * A collapsed-rail section: the icon is the section's real link, and hovering
 * or focusing it opens a flyout with the section's pages. The flyout is not
 * portalled, so its links sit right after the icon in the tab order (Tab walks
 * icon → pages → next icon); Arrow Right/Down jumps in, Esc closes and puts
 * focus back on the icon.
 */
const SidebarFlyout: React.FC<{
  section: NavSection;
  badge?: React.ReactNode;
}> = ({ section, badge }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  // Focus we put back on the icon ourselves must not reopen the flyout.
  const suppressFocusOpen = useRef(false);

  const active = isSectionActive(section, location);
  const Icon = section.icon;

  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(
      () => setOpen(false),
      CLOSE_DELAY_MS,
    );
  };
  const returnFocus = () => {
    suppressFocusOpen.current = true;
    triggerRef.current?.focus();
    suppressFocusOpen.current = false;
  };

  /* Radix's popover content loops Tab inside itself (its FocusScope has
   * `loop`), which would trap keyboard users in the flyout. At the edges we
   * move focus on ourselves: past the last page to whatever follows this
   * section, before the first back to the icon. */
  const onContentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const links = focusables(contentRef.current);
    const idx = links.indexOf(document.activeElement as HTMLElement);
    const last = links.length - 1;
    if (e.key === 'Tab' && !e.shiftKey && idx === last && wrapperRef.current) {
      e.preventDefault();
      setOpen(false);
      nextTabbableAfter(wrapperRef.current)?.focus();
    } else if (
      (e.key === 'Tab' && e.shiftKey && idx === 0) ||
      e.key === 'ArrowLeft'
    ) {
      e.preventDefault();
      setOpen(false);
      returnFocus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      links[(idx + 1) % links.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx <= 0) returnFocus();
      else links[idx - 1]?.focus();
    }
  };

  useEffect(() => cancelClose, []);
  // Following a link closes the flyout.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <div
        ref={wrapperRef}
        className="relative"
        onPointerEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onPointerLeave={scheduleClose}
        onFocus={() => {
          if (suppressFocusOpen.current) return;
          cancelClose();
          setOpen(true);
        }}
        onBlur={(e) => {
          if (!wrapperRef.current?.contains(e.relatedTarget as Node | null)) {
            setOpen(false);
          }
        }}
      >
        <PopoverPrimitive.Anchor asChild>
          <Link
            ref={triggerRef}
            to={section.to}
            aria-label={section.label}
            aria-haspopup="true"
            aria-expanded={open}
            aria-current={active ? 'page' : undefined}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                setOpen(true);
                // The content mounts on open; focus once it's there.
                window.requestAnimationFrame(() =>
                  focusables(contentRef.current)[0]?.focus(),
                );
              }
            }}
            className={navItemClass(active, true)}
          >
            {Icon && <Icon className="size-5 shrink-0" aria-hidden />}
            {badge}
          </Link>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Content
          ref={contentRef}
          side="right"
          align="start"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Moving between the icon and its pages is not "outside".
          onFocusOutside={(e) => {
            if (wrapperRef.current?.contains(e.target as Node))
              e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (wrapperRef.current?.contains(e.target as Node))
              e.preventDefault();
          }}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={() => {
            if (wrapperRef.current?.contains(document.activeElement)) {
              returnFocus();
            }
          }}
          onKeyDown={onContentKeyDown}
          className="z-50 min-w-48 rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-[4px_4px_16px_rgba(0,0,0,0.07)] outline-none"
        >
          <p className="px-2.5 pt-1 pb-1.5 text-xs font-semibold uppercase tracking-wide text-marble-bg-500">
            {section.label}
          </p>
          <ul className="flex flex-col gap-0.5">
            {(section.children ?? []).map((child) => {
              const childActive = isNavItemActive(child, location);
              return (
                <li key={child.id}>
                  <Link
                    to={child.to}
                    aria-current={childActive ? 'page' : undefined}
                    className={cn(
                      'block rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-marble-blue-500',
                      childActive
                        ? 'bg-marble-blue-50 font-semibold text-marble-blue-700 dark:bg-marble-bg-100'
                        : 'text-foreground hover:bg-marble-bg-50',
                    )}
                  >
                    {child.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </PopoverPrimitive.Content>
      </div>
    </PopoverPrimitive.Root>
  );
};

export default SidebarFlyout;
